import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActions = ({ 
  onExportReports = () => {},
  onGenerateBulkAI = () => {},
  onViewAlerts = () => {},
  onPatientSearch = () => {},
  className = ""
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);

  const handleExportReports = async () => {
    setIsExporting(true);
    try {
      await onExportReports();
      // Simulate export process
      setTimeout(() => {
        setIsExporting(false);
      }, 2000);
    } catch (error) {
      setIsExporting(false);
    }
  };

  const handleGenerateBulkAI = async () => {
    setIsGeneratingAI(true);
    try {
      await onGenerateBulkAI();
      // Simulate AI generation process
      setTimeout(() => {
        setIsGeneratingAI(false);
      }, 3000);
    } catch (error) {
      setIsGeneratingAI(false);
    }
  };

  const quickActionItems = [
    {
      id: 'export-reports',
      title: 'Export Reports',
      description: 'Download patient data and analytics',
      icon: 'Download',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      action: handleExportReports,
      loading: isExporting,
      variant: 'outline'
    },
    {
      id: 'bulk-ai-analysis',
      title: 'Bulk AI Analysis',
      description: 'Generate AI summaries for all patients',
      icon: 'Brain',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      action: handleGenerateBulkAI,
      loading: isGeneratingAI,
      variant: 'default'
    },
    {
      id: 'view-alerts',
      title: 'View All Alerts',
      description: 'Check urgent patient notifications',
      icon: 'Bell',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      action: onViewAlerts,
      loading: false,
      variant: 'outline'
    },
    {
      id: 'advanced-search',
      title: 'Advanced Search',
      description: 'Find patients with specific criteria',
      icon: 'Search',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      action: onPatientSearch,
      loading: false,
      variant: 'ghost'
    }
  ];

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Quick Actions</h2>
          <p className="text-sm text-muted-foreground">Common provider tasks and tools</p>
        </div>
        <Icon name="Zap" size={20} color="var(--color-primary)" />
      </div>
      {/* Desktop Grid Layout */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickActionItems?.map((item) => (
          <div
            key={item?.id}
            className="group p-4 border border-border rounded-lg hover:shadow-soft transition-all duration-200 hover:border-primary/20"
          >
            <div className="flex flex-col items-center text-center space-y-3">
              <div className={`w-12 h-12 ${item?.bgColor} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                <Icon 
                  name={item?.icon} 
                  size={20} 
                  color={`var(--color-${item?.color?.replace('text-', '')})`}
                />
              </div>
              
              <div className="space-y-1">
                <h3 className="font-medium text-foreground text-sm">{item?.title}</h3>
                <p className="text-xs text-muted-foreground">{item?.description}</p>
              </div>
              
              <Button
                variant={item?.variant}
                size="sm"
                onClick={item?.action}
                loading={item?.loading}
                className="w-full"
              >
                {item?.loading ? 'Processing...' : 'Execute'}
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Mobile List Layout */}
      <div className="md:hidden space-y-3">
        {quickActionItems?.map((item) => (
          <div
            key={item?.id}
            className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 ${item?.bgColor} rounded-lg flex items-center justify-center`}>
                <Icon 
                  name={item?.icon} 
                  size={18} 
                  color={`var(--color-${item?.color?.replace('text-', '')})`}
                />
              </div>
              <div>
                <h3 className="font-medium text-foreground text-sm">{item?.title}</h3>
                <p className="text-xs text-muted-foreground">{item?.description}</p>
              </div>
            </div>
            
            <Button
              variant={item?.variant}
              size="sm"
              onClick={item?.action}
              loading={item?.loading}
              iconName={item?.loading ? undefined : 'ChevronRight'}
              iconPosition="right"
            >
              {item?.loading ? 'Processing...' : 'Go'}
            </Button>
          </div>
        ))}
      </div>
      {/* Status Messages */}
      {(isExporting || isGeneratingAI) && (
        <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center space-x-3">
            <div className="animate-spin">
              <Icon name="Loader2" size={16} color="var(--color-primary)" />
            </div>
            <div className="text-sm text-foreground">
              {isExporting && "Preparing your reports for download..."}
              {isGeneratingAI && "Generating AI analysis for all patients..."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActions;